/**
 * AttributionService - Manages data attribution and licensing compliance
 */

export class AttributionService {
  constructor() {
    this.attributions = new Map();
    this.licenses = new Map();
    this.displayedAttributions = new Set();
    this.initializeAttributions();
  }

  /**
   * Initialize all data attributions and licenses
   */
  initializeAttributions() {
    // Map tile attributions
    this.attributions.set('openstreetmap', {
      name: 'OpenStreetMap',
      url: 'https://www.openstreetmap.org/copyright',
      text: '© OpenStreetMap contributors',
      license: 'ODbL',
      required: true,
      position: 'bottom-right'
    });

    // Biome data attributions
    this.attributions.set('wwf_ecoregions', {
      name: 'WWF Terrestrial Ecoregions',
      url: 'https://www.worldwildlife.org/pages/conservation-science-data-and-tools',
      text: 'Ecoregions data courtesy of World Wildlife Fund',
      license: 'CC-BY-4.0',
      required: true,
      position: 'info-panel'
    });

    this.attributions.set('natural_earth', {
      name: 'Natural Earth',
      url: 'https://www.naturalearthdata.com/',
      text: 'Geographic data from Natural Earth',
      license: 'Public Domain',
      required: false,
      position: 'footer'
    });

    // Climate data attributions
    this.attributions.set('koppen_climate', {
      name: 'Köppen Climate Classification',
      url: 'https://en.wikipedia.org/wiki/K%C3%B6ppen_climate_classification',
      text: 'Climate classification based on Köppen system',
      license: 'Public Domain',
      required: false,
      position: 'info-panel'
    });

    // Educational content attributions
    this.attributions.set('educational_content', {
      name: 'LandGrid.AI Educational Team',
      url: 'https://landgrid.ai',
      text: 'Educational content developed by LandGrid.AI',
      license: 'CC-BY-SA-4.0',
      required: true,
      position: 'footer'
    });

    // Libraries and dependencies
    this.attributions.set('leaflet', {
      name: 'Leaflet',
      url: 'https://leafletjs.com/',
      text: 'Interactive maps powered by Leaflet',
      license: 'BSD-2-Clause',
      required: false,
      position: 'footer'
    });

    this.attributions.set('html2canvas', {
      name: 'html2canvas',
      url: 'https://html2canvas.hertzen.com/',
      text: 'Screenshot functionality by html2canvas',
      license: 'MIT',
      required: false,
      position: 'footer'
    });

    // Initialize license information
    this.initializeLicenses();
  }

  /**
   * Initialize license details
   */
  initializeLicenses() {
    this.licenses.set('ODbL', {
      name: 'Open Database License',
      url: 'https://opendatacommons.org/licenses/odbl/',
      description: 'Allows sharing and adaptation with attribution and share-alike requirements',
      attribution_required: true,
      share_alike: true,
      commercial_use: true
    });

    this.licenses.set('CC-BY-4.0', {
      name: 'Creative Commons Attribution 4.0 International',
      url: 'https://creativecommons.org/licenses/by/4.0/',
      description: 'Allows sharing and adaptation with attribution',
      attribution_required: true,
      share_alike: false,
      commercial_use: true
    });

    this.licenses.set('CC-BY-SA-4.0', {
      name: 'Creative Commons Attribution-ShareAlike 4.0 International',
      url: 'https://creativecommons.org/licenses/by-sa/4.0/',
      description: 'Allows sharing and adaptation with attribution and share-alike requirements',
      attribution_required: true,
      share_alike: true,
      commercial_use: true
    });

    this.licenses.set('Public Domain', {
      name: 'Public Domain',
      url: 'https://creativecommons.org/publicdomain/',
      description: 'No rights reserved - free for any use',
      attribution_required: false,
      share_alike: false,
      commercial_use: true
    });

    this.licenses.set('MIT', {
      name: 'MIT License',
      url: 'https://opensource.org/licenses/MIT',
      description: 'Permissive license allowing commercial and private use',
      attribution_required: true,
      share_alike: false,
      commercial_use: true
    });

    this.licenses.set('BSD-2-Clause', {
      name: 'BSD 2-Clause "Simplified" License',
      url: 'https://opensource.org/licenses/BSD-2-Clause',
      description: 'Permissive license allowing commercial use with attribution',
      attribution_required: true,
      share_alike: false,
      commercial_use: true
    });
  }

  /**
   * Add attribution display to the page
   */
  displayAttributions() {
    // Add map attribution (always visible)
    this.displayMapAttribution();

    // Add info panel attributions
    this.displayInfoPanelAttributions();

    // Add footer attributions
    this.displayFooterAttributions();

    console.log('All required attributions displayed');
  }

  /**
   * Display map attribution
   */
  displayMapAttribution() {
    const osmAttribution = this.attributions.get('openstreetmap');

    // Leaflet automatically handles map tile attribution
    // We just ensure our attribution is included
    const mapElement = document.getElementById('map');
    if (mapElement && !this.displayedAttributions.has('openstreetmap')) {
      // Add custom attribution control if needed
      const existingAttribution = mapElement.querySelector('.leaflet-control-attribution');
      if (existingAttribution && !existingAttribution.textContent.includes('LandGrid.AI')) {
        const separator = existingAttribution.innerHTML ? ' | ' : '';
        existingAttribution.innerHTML += `${separator}<a href="https://landgrid.ai">LandGrid.AI</a>`;
      }

      this.displayedAttributions.add('openstreetmap');
    }
  }

  /**
   * Display attributions in info panel
   */
  displayInfoPanelAttributions() {
    const infoPanelAttributions = Array.from(this.attributions.values())
      .filter(attr => attr.position === 'info-panel' && attr.required);

    if (infoPanelAttributions.length > 0) {
      this.createAttributionSection('info-panel', infoPanelAttributions);
    }
  }

  /**
   * Display footer attributions
   */
  displayFooterAttributions() {
    const footerAttributions = Array.from(this.attributions.values())
      .filter(attr => attr.position === 'footer');

    if (footerAttributions.length > 0) {
      this.createFooterAttribution(footerAttributions);
    }
  }

  /**
   * Create attribution section
   */
  createAttributionSection(containerId, attributions) {
    const container = document.getElementById(containerId);
    if (!container) return;

    // Check if attribution section already exists
    let attributionDiv = container.querySelector('.attribution-section');
    if (!attributionDiv) {
      attributionDiv = document.createElement('div');
      attributionDiv.className = 'attribution-section';
      attributionDiv.style.cssText = `
        border-top: 1px solid #e0e0e0;
        margin-top: 12px;
        padding-top: 8px;
        font-size: 11px;
        color: #666;
        line-height: 1.3;
      `;
      container.appendChild(attributionDiv);
    }

    // Add attributions
    const attributionLinks = attributions.map(attr => {
      return `<a href="${attr.url}" target="_blank" rel="noopener noreferrer">${attr.text}</a>`;
    }).join(' • ');

    attributionDiv.innerHTML = `<div style="margin-bottom: 4px;">Data Sources:</div>${attributionLinks}`;

    // Mark as displayed
    attributions.forEach(attr => {
      const key = Array.from(this.attributions.entries()).find(([k, v]) => v === attr)?.[0];
      if (key) this.displayedAttributions.add(key);
    });
  }

  /**
   * Create footer attribution
   */
  createFooterAttribution(attributions) {
    // Check if footer already exists
    let footer = document.getElementById('app-footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.id = 'app-footer';
      footer.style.cssText = `
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: rgba(255, 255, 255, 0.95);
        border-top: 1px solid #e0e0e0;
        padding: 8px 16px;
        font-size: 11px;
        color: #666;
        z-index: 500;
        text-align: center;
        line-height: 1.3;
      `;

      // Add toggle button
      const toggleButton = document.createElement('button');
      toggleButton.textContent = 'Show Attributions';
      toggleButton.style.cssText = `
        background: none;
        border: none;
        color: #666;
        text-decoration: underline;
        cursor: pointer;
        font-size: 11px;
        padding: 0;
        margin-right: 8px;
      `;

      const attributionContent = document.createElement('div');
      attributionContent.style.display = 'none';
      attributionContent.style.marginTop = '4px';

      toggleButton.addEventListener('click', () => {
        const isVisible = attributionContent.style.display !== 'none';
        attributionContent.style.display = isVisible ? 'none' : 'block';
        toggleButton.textContent = isVisible ? 'Show Attributions' : 'Hide Attributions';
      });

      footer.appendChild(toggleButton);
      footer.appendChild(attributionContent);

      document.body.appendChild(footer);
    }

    // Add attribution content
    const attributionContent = footer.querySelector('div[style*="margin-top"]');
    if (attributionContent) {
      const attributionLinks = attributions.map(attr => {
        return `<a href="${attr.url}" target="_blank" rel="noopener noreferrer">${attr.text}</a>`;
      }).join(' • ');

      attributionContent.innerHTML = attributionLinks;
    }

    // Mark as displayed
    attributions.forEach(attr => {
      const key = Array.from(this.attributions.entries()).find(([k, v]) => v === attr)?.[0];
      if (key) this.displayedAttributions.add(key);
    });
  }

  /**
   * Get license information
   */
  getLicenseInfo(licenseKey) {
    return this.licenses.get(licenseKey);
  }

  /**
   * Get all attributions
   */
  getAllAttributions() {
    return Array.from(this.attributions.entries()).map(([key, value]) => ({
      key,
      ...value
    }));
  }

  /**
   * Check compliance status
   */
  checkCompliance() {
    const requiredAttributions = Array.from(this.attributions.entries())
      .filter(([key, attr]) => attr.required);

    const missingAttributions = requiredAttributions
      .filter(([key, attr]) => !this.displayedAttributions.has(key));

    return {
      compliant: missingAttributions.length === 0,
      missing: missingAttributions.map(([key, attr]) => ({ key, ...attr })),
      displayed: Array.from(this.displayedAttributions),
      total_required: requiredAttributions.length
    };
  }

  /**
   * Generate attribution report
   */
  generateAttributionReport() {
    const compliance = this.checkCompliance();

    const report = {
      timestamp: new Date().toISOString(),
      compliance_status: compliance.compliant ? 'COMPLIANT' : 'NON_COMPLIANT',
      summary: {
        total_data_sources: this.attributions.size,
        required_attributions: compliance.total_required,
        displayed_attributions: compliance.displayed.length,
        missing_attributions: compliance.missing.length
      },
      data_sources: this.getAllAttributions().map(attr => ({
        name: attr.name,
        license: attr.license,
        required: attr.required,
        displayed: this.displayedAttributions.has(attr.key),
        license_details: this.getLicenseInfo(attr.license)
      })),
      missing_attributions: compliance.missing,
      recommendations: this.generateRecommendations(compliance)
    };

    return report;
  }

  /**
   * Generate compliance recommendations
   */
  generateRecommendations(compliance) {
    const recommendations = [];

    if (!compliance.compliant) {
      recommendations.push('Display all required attributions to achieve compliance');

      compliance.missing.forEach(missing => {
        recommendations.push(`Add attribution for ${missing.name} (${missing.license} license)`);
      });
    }

    // Check for attribution best practices
    const oshAttributions = Array.from(this.attributions.values())
      .filter(attr => ['MIT', 'BSD-2-Clause', 'Apache-2.0'].includes(attr.license));

    if (oshAttributions.length > 0) {
      recommendations.push('Consider consolidating open source library attributions in a dedicated section');
    }

    // Check for CC license compliance
    const ccAttributions = Array.from(this.attributions.values())
      .filter(attr => attr.license.startsWith('CC-'));

    if (ccAttributions.length > 0) {
      recommendations.push('Ensure Creative Commons attributions include license version and link');
    }

    return recommendations;
  }

  /**
   * Export attribution data for legal review
   */
  exportAttributionData() {
    const report = this.generateAttributionReport();
    const exportData = {
      ...report,
      export_purpose: 'Legal compliance review',
      license_texts: Object.fromEntries(
        Array.from(this.licenses.entries()).map(([key, license]) => [
          key,
          {
            name: license.name,
            url: license.url,
            description: license.description,
            requirements: {
              attribution: license.attribution_required,
              share_alike: license.share_alike,
              commercial_use: license.commercial_use
            }
          }
        ])
      )
    };

    return exportData;
  }

  /**
   * Show attribution modal
   */
  showAttributionModal() {
    const modal = document.createElement('div');
    modal.className = 'attribution-modal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    const content = document.createElement('div');
    content.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 600px;
      max-height: 80vh;
      overflow-y: auto;
      margin: 20px;
    `;

    const report = this.generateAttributionReport();

    content.innerHTML = `
      <h2 style="margin-top: 0;">Data Attributions & Licenses</h2>
      <p>This application uses data and libraries from various sources. Below are the required attributions:</p>

      <div style="margin-bottom: 20px;">
        <h3>Data Sources:</h3>
        ${report.data_sources.filter(ds => ds.required).map(ds => `
          <div style="margin-bottom: 12px; padding: 8px; background: #f5f5f5; border-radius: 4px;">
            <strong><a href="${this.attributions.get(ds.key)?.url}" target="_blank">${ds.name}</a></strong><br>
            <span style="font-size: 12px; color: #666;">${ds.license} License</span><br>
            <span style="font-size: 11px;">${ds.license_details?.description || ''}</span>
          </div>
        `).join('')}
      </div>

      <div style="margin-bottom: 20px;">
        <h3>Open Source Libraries:</h3>
        ${report.data_sources.filter(ds => !ds.required && ds.license !== 'Public Domain').map(ds => `
          <div style="margin-bottom: 8px;">
            <strong><a href="${this.attributions.get(ds.key)?.url}" target="_blank">${ds.name}</a></strong> -
            <span style="font-size: 12px; color: #666;">${ds.license}</span>
          </div>
        `).join('')}
      </div>

      <div style="text-align: center;">
        <button onclick="this.closest('.attribution-modal').remove()" style="
          background: #76c893;
          color: white;
          border: none;
          border-radius: 6px;
          padding: 10px 20px;
          cursor: pointer;
          font-size: 14px;
        ">Close</button>
      </div>
    `;

    modal.appendChild(content);

    // Close on outside click
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });

    document.body.appendChild(modal);
  }

  /**
   * Add menu item for attributions
   */
  addAttributionMenuItem() {
    // Add to controls or create a menu
    const controls = document.getElementById('controls');
    if (controls) {
      const attributionBtn = document.createElement('button');
      attributionBtn.className = 'control-btn';
      attributionBtn.title = 'View data attributions and licenses';
      attributionBtn.innerHTML = '<span class="btn-icon">ⓘ</span><span class="btn-text">About</span>';

      attributionBtn.addEventListener('click', () => {
        this.showAttributionModal();
      });

      controls.appendChild(attributionBtn);
    }
  }

  /**
   * Initialize attribution system
   */
  initialize() {
    // Display all attributions
    this.displayAttributions();

    // Add attribution menu item
    this.addAttributionMenuItem();

    // Log compliance status
    const compliance = this.checkCompliance();
    if (compliance.compliant) {
      console.log('✅ Attribution compliance: PASSED');
    } else {
      console.warn('⚠️ Attribution compliance: FAILED', compliance.missing);
    }
  }
}
